import { TableRow as MUITableRow, styled } from "@material-ui/core";
import classNames from "classnames";
import TableCell from "./TableCell";
import type { EditedRowProps, TableProps, TableRowDefinition } from "./types";

interface TableRowProps {
  row: TableRowDefinition;
  index?: number;
  className?: string;
}

// Give the row an explicit opaque background so sticky cells using
// `backgroundColor: inherit` stay opaque over horizontally-scrolled cells.
// MUI's hover, `Mui-selected`, and consumer-provided `rowClass` styles
// override this — and the sticky cell inherits the override, so hover /
// selected / custom row backgrounds carry through to the sticky column.
// MUI's own hover/selected backgrounds are alpha blends (e.g.
// `rgba(0,0,0,0.04)`), which a sticky cell composites over the cells
// scrolled beneath it — the hidden value ghosts through. Re-state them as
// the visually-equivalent solid greys; `&&` beats MUI's two-class rule.
const Row = styled(MUITableRow)(({ theme }) => ({
  borderBottom: "1px solid rgba(224, 224, 224, 1)",
  backgroundColor: theme.palette.background.paper,
  "&&.MuiTableRow-hover:hover": {
    backgroundColor: theme.palette.grey[100],
  },
  "&&.Mui-selected, &&.Mui-selected:hover": {
    backgroundColor: theme.palette.grey[200],
  },
}));

const TableRow = ({
  row,
  index,
  className,
  onRowClick,
  hover = true,
  columns,
  ...tableProps
}: TableRowProps & TableProps & EditedRowProps) => {
  const { classes } = tableProps;
  return (
    <Row
      hover={hover}
      data-testid={`row-${index}`}
      className={classNames(classes?.rowClass?.(row, index), className)}
      onClick={() => onRowClick?.(row, index)}
      style={onRowClick && { cursor: "pointer" }}
    >
      {columns.map((column, idx) => (
        <TableCell
          key={`${column.field}-${row.id}-cell`}
          row={row}
          column={column}
          index={index}
          cellIndex={idx}
          columns={columns}
          {...tableProps}
        />
      ))}
    </Row>
  );
};

TableRow.defaultProps = {
  className: null,
  index: null,
};

export default TableRow;
